// src/context/CartContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();
const CART_STORAGE_KEY = 'jcb_cart';

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState(() => {
        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Migración: itemType 'machine' → 'machineRequest'
                return parsed.map(item =>
                    item.itemType === 'machine'
                        ? { ...item, itemType: 'machineRequest' }
                        : item
                );
            }
        } catch (error) {
            console.error('Error loading cart from localStorage:', error);
        }
        return [];
    });

    useEffect(() => {
        try {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
        } catch (error) {
            console.error('Error saving cart to localStorage:', error);
        }
    }, [cartItems]);

    // ============================================================
    // 1) SOLICITUD DE REFACCIONES (formulario por maquinaria)
    // ============================================================
    const addMachineRequest = (data) => {
        const machineryType = (data.machineryType || '').trim();
        const model = (data.model || '').trim();
        const year = (data.year || '').trim();
        const area = (data.area || '').trim();

        const id = `machine-request|${machineryType}|${model}|${year}|${area}`;

        setCartItems(prev => {
            const existing = prev.find(i => i.itemType === 'machineRequest' && i.id === id);
            if (existing) return prev;

            return [...prev, {
                itemType: 'machineRequest',
                id,
                machineryType,
                model,
                year,
                area,
                quantity: 1,
            }];
        });
    };

    // ============================================================
    // 2) REFACCIONES
    // ============================================================
    const addSparePart = (part, context = {}) => {
        let contextKey;
        if (context.source === 'machine') {
            contextKey = `machine|${context.machineryType || ''}|${context.model || ''}|${context.year || ''}|${context.area || ''}`;
        } else if (context.source === 'spare-parts-list') {
            contextKey = 'spare-parts-list';
        } else {
            contextKey = 'partNumber';
        }

        setCartItems(prev => {
            const existing = prev.find(
                i => i.itemType === 'sparePart'
                    && i.id === part.id
                    && i.contextKey === contextKey
            );

            if (existing) {
                return prev.map(i =>
                    i.itemType === 'sparePart'
                        && i.id === part.id
                        && i.contextKey === contextKey
                        ? { ...i, quantity: i.quantity + 1 }
                        : i
                );
            }

            return [...prev, {
                itemType: 'sparePart',
                id: part.id,
                name: part.name,
                partNumber: part.partNumber || null,
                price: part.price || 0,
                quantity: 1,
                contextKey,
                context: {
                    source: context.source || 'partNumber',
                    machineryType: context.machineryType || null,
                    model: context.model || null,
                    year: context.year || null,
                    area: context.area || null,
                },
            }];
        });
    };

    // ============================================================
    // 3) MAQUINARIA
    // ============================================================
    const addMachinery = (item, context = {}) => {
        const contextKey = `machinery|${item.machineryId ?? 'generic'}`;

        setCartItems(prev => {
            const existing = prev.find(
                i => i.itemType === 'machinery' && i.id === item.id
            );

            if (existing) {
                return prev.map(i =>
                    i.itemType === 'machinery' && i.id === item.id
                        ? { ...i, quantity: i.quantity + 1 }
                        : i
                );
            }

            return [...prev, {
                itemType: 'machinery',
                id: item.id,
                name: item.name,
                price: item.price || 0,
                quantity: 1,
                machineryId: item.machineryId || null,
                machineryType: item.machineryType || context.machineryType || null,
                model: item.model || context.model || null,
                contextKey,
            }];
        });
    };

    // Alias por compatibilidad (MachineryDetail, etc.)
    const addToCart = addSparePart;

    const removeFromCart = (id, contextKey) => {
        setCartItems(prev => prev.filter(item => {
            if (item.itemType === 'machineRequest') return item.id !== id;
            if (item.itemType === 'machinery') return item.id !== id;
            return !(item.id === id && item.contextKey === contextKey);
        }));
    };

    const updateQuantity = (id, quantity, contextKey) => {
        if (quantity <= 0) {
            removeFromCart(id, contextKey);
            return;
        }
        setCartItems(prev =>
            prev.map(item => {
                if (item.itemType === 'machineRequest') return item;
                return item.id === id && item.contextKey === contextKey
                    ? { ...item, quantity }
                    : item;
            })
        );
    };

    const clearCart = () => setCartItems([]);

    // ============================================================
    // Totales
    // ============================================================
    const totalItems = cartItems.reduce((sum, item) => {
        if (item.itemType === 'machineRequest') return sum + 1;
        return sum + (item.quantity || 1);
    }, 0);

    const totalPrice = cartItems.reduce((sum, item) => {
        if (item.itemType === 'machineRequest') return sum;
        return sum + (item.price || 0) * (item.quantity || 1);
    }, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addMachineRequest,
            addSparePart,
            addMachinery,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            totalItems,
            totalPrice,
        }}>
            {children}
        </CartContext.Provider>
    );
};